import { onDisconnect, ref, remove, set } from "firebase/database";
import { deleteDoc, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { database, db } from "../firebase";

const PRESENCE_USERS = "presence/users";
const PRESENCE_GUESTS = "presence/guests";
const GUEST_ID_KEY = "up-presence-guest-id";

/** Firestore collection names when using Firestore for presence (no Realtime Database). */
export const PRESENCE_COLLECTION_USERS = "presenceUsers";
export const PRESENCE_COLLECTION_GUESTS = "presenceGuests";

/** True when presence uses Firestore (no RTDB). Admin can use this to show counts via Firestore. */
export const USE_FIRESTORE_PRESENCE = !database;

const HEARTBEAT_MS = 45 * 1000;

let heartbeatTimer = null;

const getOrCreateGuestId = () => {
  if (typeof sessionStorage === "undefined") return null;
  let id = sessionStorage.getItem(GUEST_ID_KEY);
  if (!id) {
    id = `g_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    sessionStorage.setItem(GUEST_ID_KEY, id);
  }
  return id;
};

const clearHeartbeat = () => {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
};

/** Firestore presence: write lastSeen and run heartbeat. */
const connectPresenceFirestore = (user) => {
  clearHeartbeat();

  if (user?.uid) {
    const guestId = typeof sessionStorage !== "undefined" ? sessionStorage.getItem(GUEST_ID_KEY) : null;
    if (guestId) {
      deleteDoc(doc(db, PRESENCE_COLLECTION_GUESTS, guestId)).catch(() => {});
      sessionStorage.removeItem(GUEST_ID_KEY);
    }
    const userRef = doc(db, PRESENCE_COLLECTION_USERS, user.uid);
    setDoc(userRef, { lastSeen: serverTimestamp() }, { merge: true }).catch(() => {});
    heartbeatTimer = setInterval(() => {
      setDoc(userRef, { lastSeen: serverTimestamp() }, { merge: true }).catch(() => {});
    }, HEARTBEAT_MS);
    return;
  }

  const guestId = getOrCreateGuestId();
  if (!guestId) return;
  const guestRef = doc(db, PRESENCE_COLLECTION_GUESTS, guestId);
  setDoc(guestRef, { lastSeen: serverTimestamp() }, { merge: true }).catch(() => {});
  heartbeatTimer = setInterval(() => {
    setDoc(guestRef, { lastSeen: serverTimestamp() }, { merge: true }).catch(() => {});
  }, HEARTBEAT_MS);
};

/**
 * Register this client as "online".
 * - With Realtime Database: uses presence/users and presence/guests, removed on disconnect.
 * - With Firestore only: uses presenceUsers and presenceGuests with a heartbeat; "online" = lastSeen in last ~2 min.
 * Call once when auth is ready (e.g. from App.js).
 */
export const connectPresence = (user) => {
  if (database) {
    clearHeartbeat();
    if (user?.uid) {
      const guestId = typeof sessionStorage !== "undefined" ? sessionStorage.getItem(GUEST_ID_KEY) : null;
      if (guestId) {
        remove(ref(database, `${PRESENCE_GUESTS}/${guestId}`)).catch(() => {});
        sessionStorage.removeItem(GUEST_ID_KEY);
      }
      const userRef = ref(database, `${PRESENCE_USERS}/${user.uid}`);
      set(userRef, { lastSeen: Date.now() }).catch(() => {});
      onDisconnect(userRef).remove().catch(() => {});
      return;
    }
    const guestId = getOrCreateGuestId();
    if (!guestId) return;
    const guestRef = ref(database, `${PRESENCE_GUESTS}/${guestId}`);
    set(guestRef, { lastSeen: Date.now() }).catch(() => {});
    onDisconnect(guestRef).remove().catch(() => {});
    return;
  }

  connectPresenceFirestore(user);
};

export const PRESENCE_PATH_USERS = PRESENCE_USERS;
export const PRESENCE_PATH_GUESTS = PRESENCE_GUESTS;
